import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { cn } from '../lib/utils.js';
import { portalHost } from '../lib/portalHost.js';
import { isRtl } from '../lib/rtl.js';
import {
  placeFiltersPopover,
  type FiltersPopoverPosition,
} from '../lib/filtersPopoverPlacement.js';

/* ===========================================================================
 * THE SURFACE A DATE CONTROL OPENS — and NOT a third popover positioner.
 *
 * §6 of docs/design-rules.md: reuse the primitive rather than building a second
 * one. The placement arithmetic here is `placeFiltersPopover` verbatim — the
 * same function `FiltersPopover` calls — and the portal target is `portalHost`,
 * the same one `FiltersPopover`, `Combobox` and `RowActions` use. Those two
 * modules already carry the reasons (a fullscreen wall board renders nothing
 * hanging off `document.body`; a panel that does not fit either way must cap
 * its own height rather than run off the glass), and a calendar hanging off a
 * toolbar button is the same object under a different name.
 *
 * WHAT IS DIFFERENT, and why this is a component rather than a prop on
 * `FiltersPopover`: that component owns a heading, a Reset link, an Apply
 * button and `FiltersGroup` children — it is the FILTER panel, not a generic
 * surface. A date popover's contents are a grid with its own keyboard model.
 * Sharing the shell and not the anatomy is what keeps the two from drifting in
 * placement while staying honest about being two different things.
 *
 * `data-portal-surface` is on the panel because `FiltersPopover`'s
 * outside-click test looks for it: a `DateRangeField` used INSIDE a filters
 * popover portals to the same host, so without the marker every click on a day
 * would read as a click outside the filter panel and shut it — the exact defect
 * `Combobox` hit and the reason the attribute exists.
 * ======================================================================== */

export interface DatePopoverProps {
  open: boolean;
  /** Closes and returns focus to the trigger — Escape, outside click, commit. */
  onDismiss: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  /** The element the outside-click test treats as "still inside" — the anchor. */
  anchorRef: RefObject<HTMLElement | null>;
  id: string;
  ariaLabel: string;
  /** A width utility. The single calendar and the range panel differ. */
  className?: string;
  children: ReactNode;
}

export function DatePopover({
  open,
  onDismiss,
  triggerRef,
  anchorRef,
  id,
  ariaLabel,
  className,
  children,
}: DatePopoverProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<FiltersPopoverPosition | null>(null);
  const [entered, setEntered] = useState(false);
  /* THE PANEL IS PORTALLED, SO IT LEAVES THE WRITING DIRECTION BEHIND.
     `dir` inherits down the DOM tree, and `portalHost()` moves this surface to
     `document.body` — so a control inside an Arabic subtree of an otherwise
     English console opens a panel that renders left-to-right, with the month
     stepper and the range band mirrored the wrong way. Reading the direction
     off the TRIGGER and stamping it on the panel is what re-attaches the two;
     it is the same measurement the placement already takes, and `lib/rtl.ts`'s
     `closest('[dir]')` then finds it from inside the grid. */
  const [rtl, setRtl] = useState(false);
  /** See `placeFiltersPopover` — the FIRST measurement, kept so a rotation
   *  cannot ratchet the panel narrower on every pass. */
  const naturalWidth = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      naturalWidth.current = null;
      return;
    }
    function place(): void {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      naturalWidth.current ??= panel.getBoundingClientRect().width;
      /* `isRtl` (nearest `[dir]` ancestor) rather than
         `getComputedStyle().direction`: the computed value is the fully correct
         answer and is not reliably inherited in jsdom, where every test of this
         component runs — see the header of lib/rtl.ts. It reaches
         `<html dir="rtl">` and any nearer override alike. */
      const triggerRtl = isRtl(trigger instanceof HTMLElement ? trigger : null);
      setRtl(triggerRtl);
      setPos(
        placeFiltersPopover({
          trigger: trigger.getBoundingClientRect(),
          naturalHeight: panel.scrollHeight,
          naturalWidth: naturalWidth.current,
          // The trigger's OWN computed direction, not an assumption about the
          // app: a date field inside an Arabic subtree of an English console
          // must still open from its own inline-start edge.
          rtl: triggerRtl,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        })
      );
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, triggerRef]);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      // Stopped, so a calendar opened inside a dialog does not take the dialog
      // down with it — same reasoning as `FiltersPopover`.
      e.stopPropagation();
      onDismiss();
    }
    function onPointerDown(e: Event): void {
      const node = e.target;
      if (node instanceof Node && anchorRef.current?.contains(node)) return;
      if (node instanceof Node && panelRef.current?.contains(node)) return;
      onDismiss();
    }
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, onDismiss, anchorRef]);

  if (!open) return null;

  return createPortal(
    <FocusTrap
      focusTrapOptions={{
        escapeDeactivates: false,
        clickOutsideDeactivates: false,
        allowOutsideClick: true,
        fallbackFocus: () => panelRef.current ?? document.body,
        returnFocusOnDeactivate: false,
      }}
    >
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-label={ariaLabel}
        tabIndex={-1}
        dir={rtl ? 'rtl' : 'ltr'}
        data-portal-surface=""
        /* Parked off-screen until measured rather than flashed at 0,0.
           `useLayoutEffect` runs before paint, so this only ever shows if the
           trigger has gone away. */
        style={{
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          ...(pos
            ? { maxHeight: pos.maxHeight, maxWidth: pos.maxWidth, overflowY: 'auto' as const }
            : {}),
        }}
        className={cn(
          /* `z-50`, NOT `z-40`, and the difference is whether this component
             works inside a `Dialog` at all.
             `Dialog` draws at `z-50` and is NOT portalled, so a `z-40` surface
             appended to the portal host renders BEHIND the dialog panel: on the
             announcement composer the calendar opened, correctly positioned,
             and was invisible — indistinguishable from a dead control, which is
             exactly how it was reported ("datepicker is not working").
             At `z-50` the tie is broken by document order, and the portal host
             is appended after the dialog, so the calendar draws over it. This
             is the same step `Combobox` takes, for the same reason — its list
             had to escape `FiltersPopover` and then draw over what it escaped.
             `DatePopover.stacking.test.tsx` pins the two together. */
          'fixed z-50',
          // The container edge, one step lighter than a control's — the panel
          // is a container, and `bg-surface` is what it sits on.
          'rounded-toolbar border border-border-subtle bg-surface p-3.5 shadow-popover',
          'focus-visible:outline-none',
          'transition-[opacity,transform] duration-fast ease-entrance motion-reduce:transition-none',
          entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0',
          className
        )}
      >
        {children}
      </div>
    </FocusTrap>,
    portalHost()
  );
}
